import { useLayoutEffect, type ReactNode } from "react";
import { useAuthStore } from "@/store/auth";
import { usePreferences } from "@/hooks/usePreferences";
import {
  applyAppearanceToRoot,
  normalizeAppearance,
  resetThemeRoot,
  type ThemeCache,
} from "@/lib/theme";

const CACHE_PREFIX = "aimp-appearance:";

function cacheTheme(userId: string, cache: ThemeCache): void {
  try {
    localStorage.setItem(`${CACHE_PREFIX}${userId}`, JSON.stringify(cache));
  } catch {
    // Storage can be unavailable in hardened/private browser contexts. The
    // server preference remains authoritative, so this only affects pre-paint.
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const userId = useAuthStore((state) => state.user?.id);
  const { preferences } = usePreferences();

  useLayoutEffect(() => {
    if (!userId || !preferences) return;
    const cache = applyAppearanceToRoot(normalizeAppearance(preferences.appearance));
    cacheTheme(userId, cache);
  }, [preferences, userId]);

  useLayoutEffect(() => () => resetThemeRoot(), []);

  return <>{children}</>;
}
